//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { User } from './UserProfileList';
import { useTranslation } from 'react-i18next';

interface UserProfileDetailsProps {
  userProp: User;
  onRevokeLicense: () => void;
  onAssignLicense: () => void;
  isCurrentUser: boolean;
  /**
   * True while the parent is still fetching loop memberships in the background.
   * Shows a "loading" hint instead of "no loops" until that fetch settles.
   */
  loopsLoading?: boolean;
}

const UserProfileDetails: React.FC<UserProfileDetailsProps> = ({
  userProp,
  onRevokeLicense,
  onAssignLicense,
  isCurrentUser,
  loopsLoading = false,
}) => {
  const { t } = useTranslation();

  // Use pre-enriched data from parent (UsersPanel) — no additional API calls needed.
  // Teamspaces arrive fully resolved with the user list and are rendered as-is.
  // Loops stream in afterwards from UserAdminLayout's background fan-out, so
  // ONLY the loops section has a pending state.
  const teamspaces = userProp.teamspaces || [];
  const loopNames = userProp.loops || [];
  const loopsPending = loopsLoading && loopNames.length === 0;

  // Map backend role names to display names
  const getDisplayRole = (role: string) => {
    if (role === 'ROLE_ADMIN' || role === 'Admin') return 'Admin';
    if (role === 'ROLE_USER' || role === 'User') return 'User';
    if (role === 'ROLE_SUPPORT' || role === 'Support') return 'Support';
    return role.replace(/^ROLE_/, '');
  };

  return (
    <div
      id="id-profile-details-container"
      data-testid="profile-details-container"
      className="space-y-6"
    >
      {/* Name, Email & Role row */}
      <div
        id="id-profile-details-info-row"
        data-testid="profile-details-info-row"
        className="flex items-start gap-8"
      >
        <div
          id="id-profile-details-name-section"
          data-testid="profile-details-name-section"
          className="w-[calc(34%-32px)]"
        >
          <h3 className="mb-1 text-sm font-medium text-gray-800 dark:text-white">
            {t('name')}
          </h3>
          <div
            id="id-profile-details-name-value"
            data-testid="profile-details-name-value"
            className="text-sm text-carbon-textPrimary dark:text-white"
          >
            {userProp.name}
          </div>
        </div>
        <div
          id="id-profile-details-email-role-section"
          data-testid="profile-details-email-role-section"
          className="flex w-[calc(66%-32px)] gap-8"
        >
          <div
            id="id-profile-details-email-wrapper"
            data-testid="profile-details-email-wrapper"
            className="flex-1"
          >
            <h3 className="mb-1 text-sm font-medium text-gray-800 dark:text-white">
              {t('email')}
            </h3>
            <div
              id="id-profile-details-email-value"
              data-testid="profile-details-email-value"
              className="text-sm text-brand-link dark:text-brand-linkDark"
            >
              {userProp.email}
            </div>
          </div>
          <div
            id="id-profile-details-role-wrapper"
            data-testid="profile-details-role-wrapper"
            className="flex-1"
          >
            <h3 className="mb-1 text-sm font-medium text-gray-800 dark:text-white">
              {t('role')}
            </h3>
            <div
              id="id-profile-details-roles-list"
              data-testid="profile-details-roles-list"
              className="flex flex-wrap gap-2"
            >
              {userProp.roles && userProp.roles.length > 0 ? (
                userProp.roles.map((role, index) => {
                  const displayRole = getDisplayRole(role);
                  return (
                    <span
                      key={index}
                      id={`id-profile-details-role-badge-${index}`}
                      data-testid={`profile-details-role-badge-${index}`}
                      className="rounded border border-carbon-border px-3 py-1 text-sm text-carbon-textPrimary dark:border-gray-500 dark:text-gray-200"
                    >
                      {displayRole}
                    </span>
                  );
                })
              ) : (
                <span
                  id="id-profile-details-no-roles"
                  data-testid="profile-details-no-roles"
                  className="text-sm text-gray-500"
                >
                  {t('no-roles')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Teamspaces & Loops row */}
      <div
        id="id-profile-details-spaces-row"
        data-testid="profile-details-spaces-row"
        className="flex items-start gap-8"
      >
        <div
          id="id-profile-details-teamspaces-section"
          data-testid="profile-details-teamspaces-section"
          className="w-[calc(34%-32px)]"
        >
          <h3 className="mb-2 text-sm font-medium text-gray-800 dark:text-white">
            {t('teamspaces')}
          </h3>
          <div
            id="id-profile-details-teamspaces-list"
            data-testid="profile-details-teamspaces-list"
            className="flex flex-wrap gap-2"
          >
            {teamspaces.length > 0 ? (
              teamspaces.map((teamspace, index) => (
                <span
                  key={index}
                  id={`id-profile-details-teamspace-badge-${index}`}
                  data-testid={`profile-details-teamspace-badge-${index}`}
                  className="rounded bg-carbon-surfaceSecondary px-3 py-1 text-sm text-carbon-textPrimary dark:bg-carbon-darkPanel dark:text-gray-200"
                >
                  {teamspace}
                </span>
              ))
            ) : (
              <span
                id="id-profile-details-no-teamspaces"
                data-testid="profile-details-no-teamspaces"
                className="text-sm text-gray-500"
              >
                {t('no-teamspaces')}
              </span>
            )}
          </div>
        </div>
        <div
          id="id-profile-details-loops-section"
          data-testid="profile-details-loops-section"
          className="w-[calc(66%-32px)]"
        >
          <h3 className="mb-2 text-sm font-medium text-gray-800 dark:text-white">
            {t('loops')}
          </h3>
          <div
            id="id-profile-details-loops-list"
            data-testid="profile-details-loops-list"
            className="flex flex-wrap gap-2"
          >
            {loopsPending ? (
              <span
                id="id-profile-details-loops-loading"
                data-testid="profile-details-loops-loading"
                className="text-sm text-gray-400"
              >
                {t('loading-trans')}
              </span>
            ) : loopNames.length > 0 ? (
              loopNames.map((loopName, index) => (
                <span
                  key={index}
                  id={`id-profile-details-loop-badge-${index}`}
                  data-testid={`profile-details-loop-badge-${index}`}
                  className="rounded bg-blue-100 px-3 py-1 text-sm text-carbon-textPrimary dark:bg-carbon-darkPanel dark:text-gray-200"
                >
                  {loopName}
                </span>
              ))
            ) : (
              <span
                id="id-profile-details-no-loops"
                data-testid="profile-details-no-loops"
                className="text-sm text-gray-500"
              >
                {t('no-loops')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileDetails;
